import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * IncentiveSplitConfig — Inline component for configuring incentive split
 * among multiple selected employees.
 *
 * Props:
 *   employees       — array of selected Employee objects { id, fullName, department, role }
 *   incentiveAmount — total incentive amount (OMR)
 *   splitConfig     — current split config { method: 'equal'|'custom', shares: { [id]: number } }
 *   onChange        — (splitConfig) => void
 */
const IncentiveSplitConfig = ({ employees = [], incentiveAmount = 0, splitConfig, onChange }) => {
  const [method, setMethod] = useState(splitConfig?.method || 'equal');
  const [shares, setShares] = useState(() => {
    if (splitConfig?.shares) return { ...splitConfig?.shares };
    // Default equal split
    const pct = employees?.length > 0 ? parseFloat((100 / employees?.length)?.toFixed(2)) : 0;
    return Object.fromEntries(employees?.map((e, i) => [
      e?.id,
      i === employees?.length - 1
        ? parseFloat((100 - pct * (employees?.length - 1))?.toFixed(2))
        : pct
    ]));
  });

  // Recalculate when employees or method changes
  useEffect(() => {
    if (method === 'equal') {
      const pct = employees?.length > 0 ? parseFloat((100 / employees?.length)?.toFixed(2)) : 0;
      const newShares = Object.fromEntries(employees?.map((e, i) => [
        e?.id,
        i === employees?.length - 1
          ? parseFloat((100 - pct * (employees?.length - 1))?.toFixed(2))
          : pct
      ]));
      setShares(newShares);
      onChange?.({ method: 'equal', shares: newShares });
    }
  }, [method, employees?.length]);

  // Notify parent on shares change (custom mode)
  useEffect(() => {
    if (method === 'custom') {
      onChange?.({ method: 'custom', shares });
    }
  }, [shares, method]);

  const totalPct = Object.values(shares)?.reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const isValid = Math.abs(totalPct - 100) < 0.01;

  const formatOMR = (v) =>
    `OMR ${(parseFloat(v) || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;

  const handleShareChange = (empId, value) => {
    setShares(prev => ({ ...prev, [empId]: value === '' ? '' : parseFloat(value) || 0 }));
  };

  if (employees?.length === 0) return null;

  // Single employee — always 100%
  if (employees?.length === 1) {
    return (
      <div className="bg-success/5 border border-success/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="User" size={14} className="text-success" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Incentive Allocation</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">{employees?.[0]?.fullName}</p>
            <p className="text-xs text-muted-foreground">{employees?.[0]?.department}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-success">{formatOMR(incentiveAmount)}</p>
            <p className="text-xs text-muted-foreground">100% of incentive</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Method selector */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Incentive Split Method
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setMethod('equal')}
            className={`
              flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all
              ${method === 'equal' ?'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40'
              }
            `}
          >
            <Icon name="Equal" size={14} />
            <div className="text-left">
              <div className="font-semibold">Equal Split</div>
              <div className="text-xs opacity-70">Divide equally</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMethod('custom')}
            className={`
              flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all
              ${method === 'custom' ?'bg-primary/10 border-primary text-primary' :'bg-background border-border text-muted-foreground hover:border-primary/40'
              }
            `}
          >
            <Icon name="Sliders" size={14} />
            <div className="text-left">
              <div className="font-semibold">Custom Split</div>
              <div className="text-xs opacity-70">Set percentages</div>
            </div>
          </button>
        </div>
      </div>
      {/* Breakdown table */}
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <div className="px-3 py-2 bg-muted/30 border-b border-border flex items-center justify-between">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Incentive Breakdown
          </span>
          <span className="text-xs text-muted-foreground">
            Total: {formatOMR(incentiveAmount)}
          </span>
        </div>

        <div className="divide-y divide-border">
          {employees?.map((emp) => {
            const pct = parseFloat(shares?.[emp?.id]) || 0;
            const amount = incentiveAmount * (pct / 100);
            return (
              <div key={emp?.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{emp?.fullName}</p>
                  <p className="text-xs text-muted-foreground">{emp?.department}</p>
                </div>
                {method === 'equal' ? (
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-success">{formatOMR(amount)}</p>
                    <p className="text-xs text-muted-foreground">{pct?.toFixed(2)}%</p>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="relative w-20">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={shares?.[emp?.id] === '' ? '' : shares?.[emp?.id]}
                        onChange={e => handleShareChange(emp?.id, e?.target?.value)}
                        className={`
                          w-full px-2 py-1.5 pr-5 bg-background border rounded-lg text-sm text-right
                          focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors
                          ${!isValid ? 'border-error/50' : 'border-border'}
                        `}
                      />
                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                    </div>
                    <div className="text-right w-24">
                      <p className="text-sm font-bold text-success">{formatOMR(amount)}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Total row */}
        <div className={`
          flex items-center justify-between px-3 py-2.5 border-t
          ${isValid ? 'bg-success/5 border-success/20' : 'bg-error/5 border-error/20'}
        `}>
          <span className="text-sm font-semibold text-foreground">Total</span>
          <div className="flex items-center gap-3">
            {method === 'custom' && (
              <span className={`text-sm font-bold ${isValid ? 'text-success' : 'text-error'}`}>
                {totalPct?.toFixed(2)}%
                {!isValid && (
                  <span className="ml-1 text-xs font-normal">
                    ({totalPct > 100 ? `+${(totalPct - 100)?.toFixed(2)}` : `-${(100 - totalPct)?.toFixed(2)}`} to reach 100%)
                  </span>
                )}
              </span>
            )}
            <span className={`text-sm font-bold ${isValid ? 'text-success' : 'text-muted-foreground'}`}>
              {formatOMR(incentiveAmount)}
            </span>
          </div>
        </div>
      </div>
      {/* Validation message */}
      {method === 'custom' && !isValid && (
        <div className="flex items-center gap-2 p-2.5 bg-error/10 border border-error/30 rounded-lg text-error text-xs">
          <Icon name="AlertCircle" size={13} className="flex-shrink-0" />
          <span>
            Total percentage must equal 100%. Currently {totalPct?.toFixed(2)}%.
            {totalPct < 100
              ? ` Add ${(100 - totalPct)?.toFixed(2)}% more.`
              : ` Remove ${(totalPct - 100)?.toFixed(2)}%.`
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default IncentiveSplitConfig;
